import SelectInput from '@brands/Components/Inputs/SelectInput/SelectInput';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import { config } from '@brands/config/config';
import useCases from '@brands/hooks/useCases';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { getOneCase } from '@brands/services/cases/getCaseById';
import { CaseType, ICase } from '@brands/services/cases/types/ICase';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectDependents } from '@brands/store/selectors/dependents';
import { updatePaymentAvailability } from '@brands/Utils/getFriendlyName';
import queryString from 'query-string';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SingleValue } from 'react-select';

import { selectBorderLessCustomStyles } from '../../../Utils/customStyles';
import EditCreditCardModal from '../../Appointments/Patient/EditCreditCardModal/EditCreditCardModal';
import UpcomingServices from '../../Dashboard/Patient/components/UpcomingServices';
import NoCareAvailable from './NoCareAvailable';
import CareJourneyStrip from './CareJourneyStrip';
import { collapseJourneyCases, isOpenJourney, useCaseJourneyLookup } from './useCaseJourneyLookup';
import styles from './styles.module.scss';

interface Option {
  value: string;
  label: string;
}

const UpcomingAppointments = (): JSX.Element => {
  const [displayingCases, setDisplayingCases] = useState<ICase[]>([]);
  const [selectedCaseFromUpcomingServices, setSelectedCaseFromUpcomingServices] = useState<ICase>();
  const [editCreditCardModal, setEditCreditCardModal] = useState(false);
  const observer = useRef<IntersectionObserver>();
  const lastMyCasesItemRef = useRef<HTMLTableRowElement>(null);
  const appliedPatientFromQuery = useRef(false);
  const { userInfo } = useAppSelector(selectAuth);
  const { dependents: listOfDependents } = useAppSelector(selectDependents);
  const [patientOptions, setPatientOptions] = React.useState<SingleValue<Option>[]>([]);

  const location = useLocation();
  const [patient, setPatient] = useState<Option>({
    value: userInfo.id.toString(),
    label: 'Myself',
  });
  const [isLoading, setIsLoading] = useState(false);

  // This screen shows one patient at a time. Journeys that haven't ended belong
  // here, running or paused; complete and canceled ones surface under Care
  // History.
  const caseJourneys = useCaseJourneyLookup([Number(patient.value)], config.features.multiStage);

  const journeyLabel = patient.value !== userInfo.id.toString() ? patient.label : undefined;

  // The strip hangs off the journey's one visible case — the list is collapsed
  // to the earliest open case of each running or paused journey.
  const journeyAnchors = useMemo(() => {
    const anchors = new Map<number, number>();
    displayingCases.forEach((caseItem) => {
      const match = caseJourneys.byCase.get(Number(caseItem.id));
      if (match && isOpenJourney(match.journey.status)) anchors.set(match.journey.case_journey_id, Number(caseItem.id));
    });
    return anchors;
  }, [displayingCases, caseJourneys]);

  // Open journeys whose cases are all filtered out of this list (one whose next
  // case still waits on intake has no case this query returns) would otherwise
  // vanish from the screen, so they keep the standalone strip.
  const unanchoredJourneys = useMemo(
    () =>
      caseJourneys.journeys.filter(
        (m) => isOpenJourney(m.journey.status) && !journeyAnchors.has(m.journey.case_journey_id),
      ),
    [caseJourneys, journeyAnchors],
  );

  const renderCaseJourneyFooter = (caseItem: ICase): React.ReactNode => {
    const match = caseJourneys.byCase.get(Number(caseItem.id));
    if (!match || journeyAnchors.get(match.journey.case_journey_id) !== Number(caseItem.id)) return null;
    return (
      <CareJourneyStrip
        attached
        patientId={match.patientId}
        journeyId={match.journey.case_journey_id}
        status={match.journey.status}
        patientLabel={journeyLabel}
      />
    );
  };

  const params = useMemo(
    () => ({
      sort_by: 'created_at' as const,
      sort_direction: 'desc' as const,
      patient_id: Number(patient.value),
      include_vc_stats: true,
      type: [
        CaseType.one_report,
        CaseType.second_opinion,
        CaseType.video_call_scheduled,
        CaseType.phone_call_scheduled,
        CaseType.care_coordination,
        CaseType.oral_health_coaching,
        CaseType.clinical_enablement,
      ],
      status: [
        'new',
        'accepted_care_advisor',
        'accepted_provider',
        'in_progress_provider',
        'in_progress_care_advisor',
        'completed_by_provider',
      ],
      include_dependents_cases: false,
    }),
    [patient],
  );

  const { cases, dataLoading, reloadAll, loadNextPage } = useCases(params, true);

  useEffect(() => {
    if (cases) {
      setDisplayingCases(collapseJourneyCases(cases, caseJourneys, params.status));
    }
  }, [cases, caseJourneys, params.status]);

  const loadMore = (): void => {
    setIsLoading(true);
    loadNextPage().then(() => {
      setIsLoading(false);
    });
  };

  const handleIntersection = (entries: IntersectionObserverEntry[]): void => {
    const lastItem = entries[0];
    if (lastItem.isIntersecting && !isLoading) {
      loadMore();
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(handleIntersection);

      const currentRef = lastMyCasesItemRef.current;
      if (currentRef) {
        observer.current.observe(currentRef);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [cases]);

  const defaultPatient = {
    value: userInfo.id.toString(),
    label: 'Myself',
    status: 'Active',
  };

  useEffect(() => {
    const fetchAndSetPatientOptions = async (): Promise<void> => {
      setIsLoading(true);
      if (userInfo.id) {
        const dependentsWithImages = await Promise.all(
          listOfDependents
            .filter((dependent) => dependent?.status !== 'Deactivated')
            .map((dependent) => ({
              ...dependent,
              label: dependent?.label ?? '',
              value: dependent?.value ?? '',
            })),
        );
        setPatientOptions([defaultPatient, ...dependentsWithImages]);
      }
      setIsLoading(false);
    };

    fetchAndSetPatientOptions();
  }, [listOfDependents, userInfo.id]);

  // Deep-links from elsewhere (e.g. the active-consultations modal) can point
  // at a dependent's case; apply that selection once options are loaded, and
  // only once, so it doesn't fight a subsequent manual dropdown change.
  useEffect(() => {
    if (appliedPatientFromQuery.current || patientOptions.length === 0) return;
    const queryParams = queryString.parse(location.search);
    const queryPatientId = queryParams.patientId;
    if (!queryPatientId) return;
    const match = patientOptions.find((option) => option?.value === String(queryPatientId));
    if (match) {
      setPatient(match as Option);
    }
    appliedPatientFromQuery.current = true;
  }, [patientOptions, location]);

  useEffect(() => {
    const queryParams = queryString.parse(location.search);
    if (!queryParams.edit) return;

    async function init(): Promise<void> {
      const appointment = await getOneCase(Number(queryParams.edit), {
        include_patient_care_advisor_conversation: true,
        include_patient_provider_conversation: true,
        include_provider_care_advisor_conversation: false,
      });
      if (appointment && updatePaymentAvailability(appointment)) {
        setSelectedCaseFromUpcomingServices(appointment);
        setEditCreditCardModal(true);
      }
    }

    init();
  }, [location]);

  return (
    <div className={styles.dashboardContainer}>
      {unanchoredJourneys.map((match) => (
        <CareJourneyStrip
          key={match.journey.case_journey_id}
          patientId={match.patientId}
          journeyId={match.journey.case_journey_id}
          status={match.journey.status}
          patientLabel={journeyLabel}
          style={{ marginBottom: 12 }}
        />
      ))}
      <div className={styles.stateContainer}>
        <p className={`${styles.yourStateTitle} fs-unmask`}>Upcoming appointments for</p>
        <SelectInput
          name="patient"
          options={patientOptions as Option[]}
          selectorClass={styles.selector}
          style={selectBorderLessCustomStyles}
          containerClass={`${styles.selectorContainer} fs-mask`}
          selectedValue={patientOptions.find((option) => option?.value === patient?.value?.toString())}
          onChange={(selected: SingleValue<Option>) => {
            if (selected) {
              setPatient(selected);
            }
          }}
        />
      </div>
      {displayingCases.length > 0 ? (
        <UpcomingServices
          displayingCases={displayingCases}
          dataLoading={dataLoading}
          reloadAll={reloadAll}
          setEditCreditCardModal={setEditCreditCardModal}
          selectedCaseFromUpcomingServices={selectedCaseFromUpcomingServices}
          setSelectedCaseFromUpcomingServices={setSelectedCaseFromUpcomingServices}
          lastMyCasesItemRef={lastMyCasesItemRef}
          isLoading={isLoading}
          renderCaseFooter={renderCaseJourneyFooter}
        />
      ) : !dataLoading ? (
        <NoCareAvailable upcomingAppointment />
      ) : (
        <Loading dotted />
      )}
      {editCreditCardModal && (
        <EditCreditCardModal
          setOpenModal={setEditCreditCardModal}
          isOpen={editCreditCardModal}
          thisCase={selectedCaseFromUpcomingServices!}
          reloadAll={reloadAll}
          refreshAll={reloadAll}
        />
      )}
    </div>
  );
};

export default UpcomingAppointments;
